import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserService } from '../auth/services/user.service';
import { Errors } from '../models/errors.model';

/**
 * Global HTTP error interceptor.
 *
 * ## 401 Handling Strategy
 *
 * There are two layers of 401 handling:
 *
 * 1. GET /user endpoint (auth initialization):
 *    - Handled by UserService.getCurrentUser() with 4XX vs 5XX logic
 *    - This interceptor SKIPS /user to avoid double-handling
 *
 * 2. All OTHER endpoints (articles, comments, profiles, etc.):
 *    - Handled HERE - any 401 means "token expired mid-session"
 *    - We call purgeAuth() to logout immediately
 *
 * Both paths result in logout on 401, but /user needs special handling
 * because it also distinguishes 5XX errors (server down → retry).
 *
 * ## Error Format
 *
 * Re-throws errors with { ...body, status } format so that:
 * - Components can access err.errors for display (e.g., validation errors)
 * - Auth logic can check err.status for error type decisions
 * - Network errors get a user-friendly fallback message
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const userService = inject(UserService);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // Global 401 handling for all endpoints EXCEPT /user
      // (token expired mid-session → logout)
      // /user is handled by UserService.getCurrentUser() with 4XX vs 5XX logic
      if (err.status === 401 && !req.url.endsWith('/user')) {
        userService.purgeAuth();
      }

       // Normalize error format to unified Errors interface
       // Preserves all server information while providing fallbacks
       const normalizedError = normalizeErrorBody(err);
       normalizedError.status = err.status;

       return throwError(() => normalizedError);
    }),
  );
};

/**
 * Normalize error responses from server into unified Errors interface.
 *
 * Handles:
 * - 409 Conflict: { title, details, status, timestamp, developerMessage }
 * - 422 Validation: { title, details, fieldErrors, status, timestamp, developerMessage }
 * - Generic errors: { message, error } or just string
 * - Network errors: { status: 0 }
 */
const normalizeErrorBody = (err: HttpErrorResponse): Errors => {
  const result: Errors = {};

  if (err.error && typeof err.error === 'object') {
    // Case 1: Server error with proper structure (409, 422, etc.)
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const serverError = err.error as any;

    // Copy server response fields
    if (serverError.title) result.title = serverError.title;
    if (serverError.details) result.details = serverError.details;
    if (serverError.timestamp) result.timestamp = serverError.timestamp;
    if (serverError.developerMessage) result.developerMessage = serverError.developerMessage;
    if (serverError.status) result.status = serverError.status;

    // Case 2: Validation errors (422)
    if (serverError.fieldErrors && typeof serverError.fieldErrors === 'object') {
      result.fieldErrors = serverError.fieldErrors;

      // Also create a flat errors object for backward compatibility
      const flattenedErrors: Record<string, string> = {};
      for (const [field, messages] of Object.entries(serverError.fieldErrors)) {
        if (Array.isArray(messages) && messages.length > 0) {
          flattenedErrors[field] = (messages as string[])[0]; // Use first error message
        }
      }
      if (Object.keys(flattenedErrors).length > 0) {
        result.errors = flattenedErrors;
      }
    }

    // Case 3: Already has errors object
    if ('errors' in serverError && serverError.errors && typeof serverError.errors === 'object') {
      result.errors = serverError.errors;
    }

    // If we have any server data, return it
    if (Object.keys(result).length > 0) {
      return result;
    }

    // Case 4: Old format or other structured error
    if ('details' in serverError && typeof serverError.details === 'string') {
      result.details = serverError.details;
      result.errors = { message: serverError.details };
      return result;
    }

    if ('message' in serverError && typeof serverError.message === 'string') {
      result.errors = { message: serverError.message };
      return result;
    }
  }

  // Case 5: String error message
  if (typeof err.error === 'string' && err.error.trim()) {
    result.errors = { message: err.error };
    return result;
  }

  // Case 6: Network error (no connection)
  result.errors = {
    network: 'Unable to connect. Please check your internet connection.'
  };
  return result;
};
